import React, { createContext, PropsWithChildren, useContext, useRef } from 'react'
import { GoogleAnalytics } from '../analytics/GoogleAnalytics'
import {
  getAttribution,
  getFirstSeenDate,
  getOrCreateClientId,
  resolveSession,
} from '../analytics/session'
import { getStatus } from '../handlers/walletActions/wallet'
import { GAEvent, GAEventName, GAUserContext } from '../interfaces/googleAnalytics'
import { logger } from '../logger'
import { useLocales } from '../state'
import { deviceInfo } from '../utils/deviceInfo'

const UNREGISTERED_TTL_MS = 60 * 1000

export const GoogleAnalyticsContext = createContext<{
  fireEvent: (name: GAEventName, eventData?: GAEvent) => Promise<void>
}>({
  fireEvent: async () => {},
})

export const GoogleAnalyticsProvider = ({ children }: PropsWithChildren) => {
  const { userLocale } = useLocales()
  const ga = useRef<Promise<GoogleAnalytics> | undefined>(undefined)
  const registered = useRef<{ value: boolean; checkedAt: number } | undefined>(undefined)

  const getClient = () => {
    if (!ga.current) {
      ga.current = getOrCreateClientId().then((clientId) => new GoogleAnalytics(clientId))
    }
    return ga.current
  }

  const isRegistered = async () => {
    const cached = registered.current
    if (cached && (cached.value || Date.now() - cached.checkedAt < UNREGISTERED_TTL_MS)) {
      return cached.value
    }

    const status = await getStatus()
    const value = status.hasVault && status.hasPassword
    registered.current = { value, checkedAt: Date.now() }
    return value
  }

  const fireEvent = async (name: GAEventName, eventData?: GAEvent) => {
    try {
      const client = await getClient()

      const userContext: GAUserContext = {
        registered: await isRegistered(),
        first_seen_date: await getFirstSeenDate(),
        region: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: userLocale.id,
        ...deviceInfo,
      }

      const attribution = await getAttribution()
      const session = await resolveSession()

      if (session.isNewSession) {
        await client.fireEvent(
          GAEventName.PLUG_SESSION_START,
          undefined,
          userContext,
          session,
          attribution
        )
      }

      await client.fireEvent(name, eventData, userContext, session, attribution)
    } catch (e) {
      logger.debug('Failed to send GA event', { originalError: e })
    }
  }

  return (
    <GoogleAnalyticsContext.Provider value={{ fireEvent }}>
      {children}
    </GoogleAnalyticsContext.Provider>
  )
}

export const useGoogleAnalytics = () => useContext(GoogleAnalyticsContext)
